import { Form, Formik } from "formik";
import { lazy } from "yup";
import { useState } from "react";
import moment from "moment/moment";
import { produce } from "immer";
import { Link } from "react-router-dom";
// @ts-expect-error pure js
import { ReactSelect } from "../UI/Select";
import Card from "../UI/Card";
import Callout from "../UI/Callout";
import Text from "../UI/Text";
import Title from "../UI/Title";
import Button from "../UI/Button";
// @ts-expect-error pure js
import Checkbox from "../UI/Checkbox";
import ModalDialog, {
  ModalDialogBody,
  ModalDialogFooter,
} from "../UI/ModalDialog";
import FormikSubmitButton from "../UI/FormikSubmitButton";
import FileInput from "../UI/FileInput";
import TransactionSplitFieldArray from "./TransactionSplitFieldArray";
// @ts-expect-error pure js
import { ModalAwareLink } from "../UI/Modal";
import { Selectable } from "../UI/Selectable";
// @ts-expect-error pure js
import { sanitizeValues, toInitialValuesFromSchema } from "../../models/utils";
import {
  getTransactionSplitValidationSchema,
  TRANSACTION_CHILD_INITIAL_VALUE,
  TRANSACTION_SPLIT_FORM_INITIAL_VALUE,
  transactionChildValidationSchema,
  transactionSplitValidationSchema,
  // @ts-expect-error pure js
} from "../../models/transactionChild";
// @ts-expect-error pure js
import { currencyFormatter } from "../../api/Functions";
// @ts-expect-error pure js
import useTransactions from "../../hooks/use-transactions";
// @ts-expect-error pure js
import usePropertyFolders from "../../hooks/use-property-folders";
// @ts-expect-error pure js
import { useBalanceTypes } from "../../hooks/use-balances";
import {
  BalanceDomain,
  CONTEXTLESS_AFFECTATION_DOMAINS,
} from "../../utils/constants/balances";
import { PropertyType } from "../../models/property";
// @ts-expect-error pure js
import { DocType } from "../../utils/constants/documents";
// @ts-expect-error pure js
import { handleAPIError } from "../../utils";
import { initializeRefacturationValues } from "../../utils/balances";
import type { Transaction } from "../../models/transaction";
import type { Bank } from "../../models/bank";
import type { Balance } from "../../models/balance";
import styles from "./TransactionSplitForm.module.css";

interface SplitTemplate {
  id: number;
  nom: string;
  splits: unknown[];
}

interface TransactionSplitSubmitValues {
  balances: Balance[];
  files?: unknown[];
  template?: string;
  update_template?: boolean;
  save_keyword?: boolean;
}

interface RefacturationValue {
  tenant_id: string | number;
  date: string;
}

interface SplitBalanceValues {
  id?: number;
  domaine?: string;
  id_property_folder?: number;
  id_profile_bailleur?: number;
  montant?: string | number;
  type?: string;
  date_transac?: string;
  comment?: string;
  periode_debut?: string;
  periode_fin?: string;
  file?: unknown;
  balances?: SplitBalanceValues[];
  refacturation_values?: RefacturationValue[];
  refacturations?: Record<string, string>;
}

interface TransactionSplitFormValues {
  balances: SplitBalanceValues[];
  files: unknown[];
  template?: string;
  update_template?: boolean;
  save_keyword?: boolean;
}

interface TransactionSplitFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  transaction: Transaction | null;
  shouldPrefillSplit?: boolean;
  splitTemplates: SplitTemplate[];
  banks: Bank[];
  onSubmit: (
    values: TransactionSplitSubmitValues,
    selected: (string | number)[],
  ) => Promise<void> | void;
}

// Un mot clé rejoue l'affectation à la synchronisation bancaire, qui ne sait rattacher
// qu'un dossier, un bien ou un locataire.
function isKeywordTarget(domaine: string | undefined): boolean {
  return (CONTEXTLESS_AFFECTATION_DOMAINS as string[]).includes(domaine ?? "");
}

function TransactionHeaderCard({
  transaction,
  banks,
}: {
  transaction: Transaction;
  banks: Bank[];
}) {
  const bank = banks.find((b) => b.unique_id === transaction.unique_account_id);

  return (
    <Card padding="sm" type="blue" className={styles.transactionCard}>
      <div className={styles.transactionInfo}>
        <Text as="span" weight="bold" className={styles.transactionDescription}>
          {transaction.description}
        </Text>
        {bank && (
          <div className={styles.transactionMeta}>
            {bank.bank_metadata?.logo && (
              <img
                className={styles.bankLogo}
                src={bank.bank_metadata.logo}
                alt={`Logo banque ${bank.nom}`}
              />
            )}
            <Text as="span" variant="deci" weight="bold">
              {bank.nom}
            </Text>
          </div>
        )}
      </div>
      <Text as="div" variant="deca" weight="bold" color="primary">
        {currencyFormatter(transaction.montant)}
      </Text>
    </Card>
  );
}

export default function TransactionSplitForm({
  open,
  onOpenChange,
  transaction,
  shouldPrefillSplit,
  splitTemplates,
  banks,
  onSubmit,
}: TransactionSplitFormProps) {
  const [showMore, setShowMore] = useState(false);
  const [error, setError] = useState("");
  const { propertyFoldersById } = usePropertyFolders({ withById: true });
  const { balanceTypes } = useBalanceTypes();
  const { transactions } = useTransactions({
    params: {
      page: 1,
      per_page: 1000,
      search: transaction?.description || undefined,
      account: transaction?.unique_account_id
        ? [transaction?.unique_account_id]
        : [],
    },
  }) as { transactions: (Transaction & Record<string, unknown>)[] };

  if (!transaction) return null;

  const similarTransactions = transactions.filter(
    (t) => t.id !== transaction.id && (t.balances?.length ?? 0) === 0,
  );

  let initialValues;
  if ((transaction.balances?.length ?? 0) > 0) {
    initialValues = toInitialValuesFromSchema(
      transactionSplitValidationSchema,
      transaction,
      TRANSACTION_SPLIT_FORM_INITIAL_VALUE,
    );
  } else if (shouldPrefillSplit) {
    initialValues = toInitialValuesFromSchema(
      transactionSplitValidationSchema,
      {
        balances: [
          {
            montant: transaction.montant,
            comment: transaction.description,
          },
        ],
      },
      TRANSACTION_SPLIT_FORM_INITIAL_VALUE,
    );
  } else {
    initialValues = TRANSACTION_SPLIT_FORM_INITIAL_VALUE;
  }

  initialValues.files = [];
  if (initialValues.balances[0]?.file) {
    initialValues.files.push(initialValues.balances[0].file);
  }

  if (initialValues.balances) {
    initialValues.balances = produce(
      initialValues.balances,
      (draft: SplitBalanceValues[]) => {
        draft.forEach((balance) => {
          balance.refacturation_values = initializeRefacturationValues(balance);
        });
      },
    );
  }

  return (
    <ModalDialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          setError("");
          setShowMore(false);
        }
        onOpenChange(nextOpen);
      }}
      size="lg"
      header={
        <div className={styles.headerContent}>
          <Title variant="hecto" weight="bold">
            Affecter et répartir une transaction
          </Title>
          <TransactionHeaderCard transaction={transaction} banks={banks} />
        </div>
      }
    >
      <Selectable items={similarTransactions}>
        <Selectable.Action
          render={(selected, resetSelected) => {
            const validationSchema = lazy(() =>
              getTransactionSplitValidationSchema(balanceTypes),
            );

            return (
              <Formik<TransactionSplitFormValues>
                initialValues={initialValues}
                onSubmit={async (values, { resetForm }) => {
                  const sanitizedValues = produce(
                    sanitizeValues(values),
                    (draft: TransactionSplitFormValues) => {
                      // La case a pu être cochée avant un changement de cible qui la masque.
                      if (!isKeywordTarget(draft.balances[0]?.domaine)) {
                        draft.save_keyword = false;
                      }
                      // `null` explicite pour retirer le justificatif de l'écriture.
                      const file = values.files[0] ?? null;
                      draft.balances = draft.balances.flatMap((balance) => {
                        if (
                          balance.domaine !== BalanceDomain.FOLDER ||
                          propertyFoldersById[balance.id_property_folder]
                            ?.property_type !== PropertyType.BIEN_NON_COMPTABLE
                        ) {
                          delete balance.balances;

                          const refacturations: Record<string, string> = {};
                          if (balance.refacturation_values) {
                            balance.refacturation_values.forEach((r) => {
                              refacturations[r.tenant_id] = r.date;
                            });
                          }
                          balance.refacturations = refacturations;
                          delete balance.refacturation_values;

                          balance.file = file;
                          return [balance];
                        }

                        return (balance.balances ?? []).map((childBalance) => {
                          const refacturations: Record<string, string> = {};
                          if (childBalance.refacturation_values) {
                            childBalance.refacturation_values.forEach((r) => {
                              refacturations[r.tenant_id] = r.date;
                            });
                          }

                          return {
                            ...childBalance,
                            type: balance.type,
                            date_transac: balance.date_transac,
                            comment: balance.comment,
                            periode_debut: balance.periode_debut,
                            periode_fin: balance.periode_fin,
                            refacturations,
                            file,
                          };
                        });
                      });
                    },
                  );

                  setError("");
                  try {
                    await onSubmit(sanitizedValues, selected);
                    resetForm();
                    resetSelected();
                  } catch (e) {
                    setError(handleAPIError(e));
                  }
                }}
                enableReinitialize={true}
                validationSchema={validationSchema}
              >
                {({ values, setFieldValue, handleSubmit }) => {
                  async function changeTemplateSplit(
                    option: { value: string | number } | null,
                  ) {
                    await setFieldValue("template", option?.value ?? null);
                    if (!option) {
                      await setFieldValue("balances", [
                        TRANSACTION_CHILD_INITIAL_VALUE,
                      ]);
                      return;
                    }

                    const balances = splitTemplates
                      .find(
                        (filtered) =>
                          filtered.id === parseInt(String(option.value)),
                      )!
                      .splits.map((template) => ({
                        ...toInitialValuesFromSchema(
                          transactionChildValidationSchema,
                          template,
                          TRANSACTION_CHILD_INITIAL_VALUE,
                        ),
                      }));
                    await setFieldValue("balances", balances);
                  }

                  const splitTemplateOptions = splitTemplates.map(
                    (template) => ({
                      value: template.id,
                      label: template.nom,
                    }),
                  );

                  const totalSplit = values.balances.reduce(
                    (acc, t) => acc + (parseFloat(String(t.montant)) || 0),
                    0,
                  );

                  const showSimilarTransactions =
                    values.balances.length <= 1 &&
                    parseFloat(String(values.balances[0]?.montant)) ===
                      transaction.montant &&
                    !values.balances[0]?.balances?.length;

                  const canSaveKeyword =
                    showSimilarTransactions &&
                    isKeywordTarget(values.balances[0]?.domaine);

                  const displayedSimilarTransactions = showMore
                    ? similarTransactions
                    : similarTransactions.slice(0, 5);

                  return (
                    <Form>
                      <ModalDialogBody className={styles.body}>
                        {splitTemplates.length > 0 && (
                          <ReactSelect
                            name="template"
                            label="Mot clefs répartition"
                            placeholder="Sélectionner un mot clefs"
                            onChange={(
                              option: { value: string | number } | null,
                            ) => changeTemplateSplit(option)}
                            options={splitTemplateOptions}
                          />
                        )}

                        <TransactionSplitFieldArray name="balances" />
                        {!isNaN(totalSplit) &&
                          (Math.abs(totalSplit) >
                          Math.abs(transaction?.montant) ? (
                            <Callout variant="danger">
                              Attention, le montant total des transactions
                              découpées est de {currencyFormatter(totalSplit)}{" "}
                              et dépasse le montant de la transaction initiale (
                              {currencyFormatter(transaction?.montant)})
                            </Callout>
                          ) : (
                            <div className={styles.totalCard}>
                              <Text as="span" weight="medium">
                                Montant total des répartitions
                              </Text>
                              <Text as="span" weight="bold" color="primary">
                                {currencyFormatter(totalSplit)}
                              </Text>
                            </div>
                          ))}

                        <FileInput
                          documentId={values.balances[0]?.id}
                          doctype={DocType.JUSTIFICATIF_BALANCE}
                          path="files"
                          label="Justificatifs"
                        />

                        {splitTemplates.length === 0 && (
                          <Callout variant="success">
                            Vous pouvez définir des templates pour découper vos
                            transactions automatiquement dans{" "}
                            <ModalAwareLink to="/bailleur/finances?tab-bank=configuration">
                              Banques → Configuration
                            </ModalAwareLink>
                          </Callout>
                        )}

                        {!showSimilarTransactions && (
                          <Callout variant="success">
                            Vous pouvez définir un mot clé, avec une clé de
                            répartition pour répartir dans les biens d'un
                            immeuble, en allant dans le menu Banques, onglet
                            configuration,{" "}
                            <Link to="/bailleur/finances?tab-bank=configuration">
                              créer un mot clé
                            </Link>
                          </Callout>
                        )}

                        {values.template &&
                          values.template !== "" &&
                          splitTemplates.length > 0 && (
                            <Checkbox name="update_template">
                              Mettre à jour le template avec ces valeurs
                            </Checkbox>
                          )}

                        <Callout variant="danger">{error}</Callout>

                        {canSaveKeyword && (
                          <Checkbox name="save_keyword">
                            Créer un mot clé pour affecter automatiquement les
                            futures transactions dont le libellé est identique à
                            celui-ci
                          </Checkbox>
                        )}

                        {similarTransactions.length > 0 &&
                          showSimilarTransactions && (
                            <div className={styles.similarSection}>
                              <Callout variant="success">
                                Nous avons trouvé les transactions suivantes
                                similaires à celle sélectionnée.
                                Sélectionnez-les pour effectuer la même
                                affectation.
                              </Callout>
                              <div className={styles.similarHeader}>
                                <label>
                                  <Selectable.Checkbox selectAll />{" "}
                                  <Text as="span" variant="deci">
                                    Tout sélectionner
                                  </Text>
                                </label>
                              </div>
                              {displayedSimilarTransactions.map((t, i) => (
                                <Card key={t.id} padding="sm">
                                  <label>
                                    <Selectable.Checkbox
                                      value={t.id}
                                      index={i}
                                    />
                                    <Text
                                      as="span"
                                      className={styles.similarLabel}
                                    >
                                      {moment(t.date_transac).format(
                                        "DD/MM/YYYY",
                                      )}{" "}
                                      - {t.description} -{" "}
                                      {currencyFormatter(t.montant)}
                                    </Text>
                                  </label>
                                </Card>
                              ))}
                              {similarTransactions.length > 5 && (
                                <div className={styles.similarMore}>
                                  <Button
                                    onClick={() => setShowMore(!showMore)}
                                    type="button"
                                    variant="secondary"
                                  >
                                    {showMore
                                      ? "Afficher moins"
                                      : `Afficher ${
                                          similarTransactions.length - 5
                                        } de plus`}
                                  </Button>
                                </div>
                              )}
                            </div>
                          )}
                      </ModalDialogBody>

                      <ModalDialogFooter
                        start={
                          <Button
                            type="button"
                            variant="secondary"
                            onClick={async () => {
                              await setFieldValue("balances", []);
                              await setFieldValue("template", "");
                              handleSubmit();
                            }}
                          >
                            Supprimer les répartitions
                          </Button>
                        }
                      >
                        <Button
                          type="button"
                          variant="secondary"
                          onClick={() => onOpenChange(false)}
                        >
                          Annuler
                        </Button>
                        <FormikSubmitButton>Enregistrer</FormikSubmitButton>
                      </ModalDialogFooter>
                    </Form>
                  );
                }}
              </Formik>
            );
          }}
        />
      </Selectable>
    </ModalDialog>
  );
}
